import { useAtom, useAtomValue } from "jotai"
import { ControlGroup } from "../ControlGroup"
import {
	currentAffectedPhotosAtom,
	currentClustersAtom,
	currentPhotoAtom,
	photosAtom,
	displayedPhotosAtom,
} from "@/store/photo/photos-state"
import { selectPhoto } from "@/store/photo/photos-selection-state"
import { useMemo, useRef, useState } from "react"
import {
	BiasType,
	defaultReferenceId,
	getDeepClusterDistance,
	getValidNearestRefId,
	getWeightsFromRefs,
	lightKeys,
	refCurrentAdjustmentsId,
} from "@/lib/ai/calibration/ai-calibration-feature"
import { isCropEqual, extractKeys } from "@/lib/ai/calibration/ai-calibration-utils"
import { getImageInfo } from "@/store/processing/image-info"
import { store } from "@/store/store"
import { Adjustments, MaskGroup } from "@/core/types"
import equal from "fast-deep-equal"
import { currentAdjustmentsAtom } from "@/store/store-history"
import { referenceImagesAtomFamily } from "@/store/style/style-state"
import { getColorMatchRuntimeInfo } from "@/store/debug/color-match-runtime-state"

export const AIDebugInfoPanel = () => {
	return (
		<ControlGroup name="AI Processing Inspector" expandKey="ai_debug_info" isExpanded>
			<AIDebugInfo />
		</ControlGroup>
	)
}

const AIDebugInfo = () => {
	const [currentPhoto] = useAtom(currentPhotoAtom)
	const currentAdjustments = useAtomValue(currentAdjustmentsAtom)
	const currentImageInfo = currentPhoto ? getImageInfo(currentPhoto.id) : null
	const currentClusters = useAtomValue(currentClustersAtom)
	const affectedPhotos = useAtomValue(currentAffectedPhotosAtom)
	const photos = store.get(photosAtom)
	const keyInfo = currentImageInfo?.keyNeighborId
		? getImageInfo(currentImageInfo.keyNeighborId)
		: currentImageInfo
	const referenceImages = store.get(referenceImagesAtomFamily())

	const weights = useMemo(() => {
		if (!currentImageInfo?.nearestReferences) {
			return { lightWeights: [0, 0, 0], colorWeights: [0, 0, 0], overallWeights: [0, 0, 0] }
		}
		return getWeightsFromRefs(currentImageInfo.nearestReferences)
	}, [currentImageInfo?.nearestReferences])

	const preRef = useRef<HTMLPreElement>(null)
	const [copied, setCopied] = useState(false)

	if (!currentPhoto || !currentImageInfo) {
		return null
	}

	let clusterMembers: string[] = []
	let keyNeighbor = currentImageInfo?.keyNeighborId
	let dScene = 0
	if (keyNeighbor === currentPhoto.id || keyNeighbor == null) {
		clusterMembers = currentClusters?.[keyNeighbor ?? ""] ?? []
		keyNeighbor = "self"
	} else {
		dScene = keyInfo ? getDeepClusterDistance(currentImageInfo, keyInfo) : -1
		keyNeighbor = keyNeighbor?.split("|")[0] ?? null
	}
	clusterMembers = clusterMembers.filter((member) => member !== currentPhoto?.id)
	clusterMembers = clusterMembers.map((member) => member.split("|")[0] ?? member)

	const verySimilarNeighborId = currentImageInfo?.features.verySimilarNeighbor
	const verySimilarNeighbor = verySimilarNeighborId ? verySimilarNeighborId.split("|")[0] : null

	let knn = currentImageInfo?.nearestReferences ?? undefined
	if (knn && knn.length > 1) {
		knn = knn.filter((ref) => ref[0] !== defaultReferenceId)
	}
	const references = knn?.map((ref) => ref[0])
	let ref1 = references?.[0]?.split("|")[0]
	let ref2 = references?.[1]?.split("|")[0]
	let ref3 = references?.[2]?.split("|")[0]
	const ref1InProject = photos[references?.[0] ?? ""] != undefined
	const ref2InProject = photos[references?.[1] ?? ""] != undefined
	const ref3InProject = photos[references?.[2] ?? ""] != undefined
	let colorTextForRef1 = ref1InProject ? " (red)" : " (not in this project)"
	let colorTextForRef2 = ref2InProject ? " (red)" : " (not in this project)"
	let colorTextForRef3 = ref3InProject ? " (red)" : " (not in this project)"
	if (ref1 === defaultReferenceId) {
		ref1 = "default zero-bias reference"
		colorTextForRef1 = ""
	}
	if (ref2 === defaultReferenceId) {
		ref2 = "default zero-bias reference"
		colorTextForRef2 = ""
	}
	if (ref3 === defaultReferenceId) {
		ref3 = "default zero-bias reference"
		colorTextForRef3 = ""
	}
	const { lightWeights, colorWeights, overallWeights } = weights
	const digits = 2

	const distances1 = knn?.[0]?.[1]
	const distances2 = knn?.[1]?.[1]
	const distances3 = knn?.[2]?.[1]
	// [sceneDist, lightDist, wbDist, metaDist, overallDist]
	const distancesText1 = distances1
		? "Scene: " +
			distances1[0]?.toFixed(digits) +
			", Light: " +
			distances1[1]?.toFixed(digits) +
			", WB: " +
			distances1[2]?.toFixed(digits) +
			", Meta: " +
			distances1[3]?.toFixed(digits) +
			", Overall: " +
			distances1[4]?.toFixed(digits) +
			", Cluster: " +
			distances1[5]?.toFixed(digits)
		: ""
	const distancesText2 = distances2
		? "Scene: " +
			distances2[0]?.toFixed(digits) +
			", Light: " +
			distances2[1]?.toFixed(digits) +
			", WB: " +
			distances2[2]?.toFixed(digits) +
			", Meta: " +
			distances2[3]?.toFixed(digits) +
			", Overall: " +
			distances2[4]?.toFixed(digits) +
			", Cluster: " +
			distances2[5]?.toFixed(digits)
		: ""
	const distancesText3 = distances3
		? "Scene: " +
			distances3[0]?.toFixed(digits) +
			", Light: " +
			distances3[1]?.toFixed(digits) +
			", WB: " +
			distances3[2]?.toFixed(digits) +
			", Meta: " +
			distances3[3]?.toFixed(digits) +
			", Overall: " +
			distances3[4]?.toFixed(digits) +
			", Cluster: " +
			distances3[5]?.toFixed(digits)
		: ""

	const keyPhoto = photos[currentImageInfo?.keyNeighborId ?? ""]
	const keyIsRef = keyPhoto?.kind === "reference"

	let algorithmText = ""
	let isAlgorithmNew = false
	let colorMatchRefId = getValidNearestRefId(currentImageInfo?.nearestReferences)
	if (!colorMatchRefId) {
		colorMatchRefId = currentImageInfo?.keyNeighborId ?? currentImageInfo?.imageId
	}
	const refInfo = colorMatchRefId ? referenceImages[colorMatchRefId] : null
	const useColorMatch = refInfo?.colorMatchReference != undefined
	const runtimeInfo = currentPhoto ? getColorMatchRuntimeInfo(currentPhoto.id) : undefined
	if (currentImageInfo?.presetId && !useColorMatch) {
		algorithmText = "preset + auto + bias + keyAdjustments"
	} else {
		isAlgorithmNew = true
		const isKeyPhoto = currentImageInfo?.keyNeighborId === currentImageInfo?.imageId
		const useOwnAutoLight =
			currentImageInfo?.features.useOwnAutoLightMap?.[currentImageInfo?.keyNeighborId ?? ""]
		if (!useOwnAutoLight && !isKeyPhoto) {
			if (keyIsRef) {
				algorithmText =
					"adapted exposure from key photo's adjustments, which is a reference."
			} else {
				algorithmText =
					"adapted exposure from key photo's adjustments, which is color matched from references."
			}
		} else {
			if (isKeyPhoto) {
				if (currentImageInfo?.presetId) {
					algorithmText = "preset + half colorMatch from references."
				} else {
					algorithmText = "full colorMatch from references."
				}
			} else {
				algorithmText =
					"on top of key photo adjustments (preset + wb), half colorMatch from references."
			}
		}
	}

	let adaptedKeyAdjustments: Partial<Adjustments> | null = null
	let ownAutoLighting: Partial<Adjustments> | null = null
	if (algorithmText.includes("key")) {
		adaptedKeyAdjustments = isAlgorithmNew
			? { ...currentAdjustments }
			: { ...currentImageInfo?.aiStyleAdjustments }
		if (isAlgorithmNew) {
			const keyColorMatchAdjustments = keyIsRef
				? keyInfo?.aiAdjustmentsMap?.[refCurrentAdjustmentsId]
				: keyInfo?.aiStyleAdjustments
			for (const key in adaptedKeyAdjustments) {
				if (key === "healingMasks" || key === "localAdjustments") {
					if (adaptedKeyAdjustments[key]?.length == 0) {
						delete adaptedKeyAdjustments[key]
					}
					continue
				}
				if (equal(adaptedKeyAdjustments[key], keyColorMatchAdjustments?.[key])) {
					delete adaptedKeyAdjustments[key]
				}
			}
		} else {
			delete adaptedKeyAdjustments.temperature
			delete adaptedKeyAdjustments.tint

			// Only exposure is adapted, other lighting parameters are the same as the key photo
			if (adaptedKeyAdjustments.exposure != undefined) {
				delete adaptedKeyAdjustments.highlights
				delete adaptedKeyAdjustments.shadows
				delete adaptedKeyAdjustments.whites
				delete adaptedKeyAdjustments.blacks
				delete adaptedKeyAdjustments.contrast
				delete adaptedKeyAdjustments.saturation
			}
			// If adaptedKeyAdjustments.exposure is undefined (not adapted), use the the photo's own auto lighting parameters
			ownAutoLighting = {
				...currentImageInfo?.aiAdjustmentsMap?.[currentImageInfo?.presetId ?? ""],
			}
			ownAutoLighting = extractKeys(ownAutoLighting, lightKeys)
		}

		if (
			adaptedKeyAdjustments.cropStraighten ===
			currentImageInfo?.features.autoCorrection.cropStraighten
		) {
			delete adaptedKeyAdjustments.cropStraighten
		}
		if (
			adaptedKeyAdjustments.crop &&
			currentImageInfo?.features.autoCorrection.crop &&
			isCropEqual(adaptedKeyAdjustments.crop, currentImageInfo?.features.autoCorrection.crop)
		) {
			delete adaptedKeyAdjustments.crop
		}
	}

	const affectedPhotosNames = affectedPhotos.map((photoId) => photoId.split("|")[0])

	const reprocessNeeded = currentImageInfo?.needApplyStyle

	const isMaskGroupArray = (v: unknown): v is MaskGroup[] => Array.isArray(v)

	const prettyPrint = (
		value: Partial<Adjustments> | Partial<BiasType>,
		excludeZeros = false,
	): string => {
		if (excludeZeros) {
			value = Object.fromEntries(Object.entries(value).filter(([_, v]) => v !== 0))
		}
		const result = JSON.stringify(
			value,
			(key: string, v: unknown): unknown => {
				if (key === "localAdjustments" && isMaskGroupArray(v)) {
					return v.map((mask) => mask.masks.map((m) => m.name).join(" + "))
				}
				if (Array.isArray(v)) {
					return `array(${v.length})` // Simplified text for arrays
				}
				if (typeof v === "number" && !Number.isInteger(v)) {
					return Number(v.toFixed(2))
				}
				return v
			},
			2,
		)
		return result
	}

	const handleCopy = async () => {
		if (preRef.current) {
			await navigator.clipboard.writeText(preRef.current.innerText)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		}
	}

	const handlePhotoClick = (photoId: string) => {
		const displayedPhotos = store.get(displayedPhotosAtom)
		const photo = displayedPhotos.find((p) => p.id === photoId)
		if (photo) {
			selectPhoto(photo)
		}
	}

	const clickableFile = (photoId: string, label: string) => {
		return (
			<a
				href="#"
				className="text-blue-400 hover:text-blue-300"
				onClick={(e) => {
					e.preventDefault()
					handlePhotoClick(photoId)
				}}
			>
				{label}
			</a>
		)
	}

	return (
		<div className="relative rounded border border-base-700 bg-base-800 text-xs">
			<button
				className="absolute right-1.5 top-1.5 z-10 rounded p-0.5 text-base-400 transition-colors hover:bg-base-600 hover:text-base-100"
				onClick={() => {
					void handleCopy()
				}}
				title="Copy to clipboard"
			>
				{copied ? (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="13"
						height="13"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<polyline points="20 6 9 17 4 12" />
					</svg>
				) : (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="13"
						height="13"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
						<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
					</svg>
				)}
			</button>
			<div className="max-h-60 overflow-y-auto p-2">
				<pre
					ref={preRef}
					className="select-text whitespace-pre-wrap font-mono text-base-300"
				>
					{currentPhoto.kind != "reference" && (
						<>
							{isAlgorithmNew ? <b>New algorithm: </b> : <b>Old algorithm: </b>}
							{algorithmText}
							{"\n\n"}
						</>
					)}
					{reprocessNeeded == true && (
						<>
							Reprocessing is not triggered due to forward-only reprocessing setting.
							{"\n\n"}
						</>
					)}
					{
						<>
							<b>Key photo (orange): </b>
							{keyNeighbor !== "self" ? (
								<>
									{clickableFile(
										currentImageInfo?.keyNeighborId ?? "",
										keyNeighbor ?? "",
									)}
									<> (dScene = {dScene.toFixed(4)})</>
								</>
							) : (
								<>{keyNeighbor}</>
							)}
							{"\n"}
						</>
					}
					{verySimilarNeighbor && verySimilarNeighborId && (
						<>
							{"\n"}
							<b>Very similar neighbor: </b>
							{clickableFile(verySimilarNeighborId, verySimilarNeighbor)}
							{"\n"}
						</>
					)}
					{adaptedKeyAdjustments &&
						currentPhoto?.kind !== "reference" &&
						keyNeighbor !== "self" && (
							<>
								{"\n"}
								<b>Adapted key adjustments: </b>
								{prettyPrint(adaptedKeyAdjustments)}
								{adaptedKeyAdjustments.exposure == undefined && ownAutoLighting && (
									<>
										{"\n"}
										<b>Use its own auto lighting parameters:</b>
										{"\n"}
										{prettyPrint(ownAutoLighting)}
									</>
								)}
								{"\n"}
								{<>Other global color adjustments are the same as the key photo.</>}
								{"\n"}
							</>
						)}

					{keyIsRef && keyNeighbor !== "self" && (
						<>
							{"\n"}
							<b>Reference is just the key photo.</b>
							{"\n"}
						</>
					)}

					{currentPhoto?.kind !== "reference" && ref1 && (
						<>
							{"\n"}
							<>
								<b>
									{"Ref 1 - "}
									{ref1InProject ? (
										clickableFile(references?.[0] ?? "", ref1)
									) : (
										<>{ref1}</>
									)}
									{colorTextForRef1 + "\n"}
								</b>
							</>
							{!isAlgorithmNew && (
								<>
									<> Light bias weight: {lightWeights[0]?.toFixed(digits)}</>
									{"\n"}
									<> WB bias weight: {colorWeights[0]?.toFixed(digits)}</>
									{"\n"}
									<> Overall bias weight: {overallWeights[0]?.toFixed(digits)}</>
									{"\n\n"}
								</>
							)}
							{isAlgorithmNew && runtimeInfo?.references[0] && (
								<>
									<>
										{" "}
										LUT blend weight:{" "}
										{runtimeInfo.references[0].blendWeight.toFixed(digits)}
									</>
									{"\n"}
									<>
										{" "}
										colorStrength:{" "}
										{runtimeInfo.references[0].colorStrength.toFixed(digits)}
									</>
									{"\n"}
									<>
										{" "}
										lightStrength:{" "}
										{runtimeInfo.references[0].lightStrength.toFixed(digits)}
									</>
									{"\n\n"}
								</>
							)}
							<>
								{distancesText1 && (
									<>
										<b>Distances to Ref 1:</b>
										{"\n" + distancesText1 + "\n"}
									</>
								)}
							</>
							{ref2 && (
								<>
									<>
										<b>
											{"\n"}
											{"Ref 2 - "}
											{ref2InProject ? (
												clickableFile(references?.[1] ?? "", ref2)
											) : (
												<>{ref2}</>
											)}
											{colorTextForRef2 + "\n"}
										</b>
									</>
									{!isAlgorithmNew && (
										<>
											<>
												{" "}
												Light bias weight:{" "}
												{lightWeights[1]?.toFixed(digits)}
											</>
											{"\n"}
											<> WB bias weight: {colorWeights[1]?.toFixed(digits)}</>
											{"\n"}
											<>
												{" "}
												Overall bias weight:{" "}
												{overallWeights[1]?.toFixed(digits)}
											</>
											{"\n\n"}
										</>
									)}
									{isAlgorithmNew && runtimeInfo?.references[1] && (
										<>
											<>
												{" "}
												LUT blend weight:{" "}
												{runtimeInfo.references[1].blendWeight.toFixed(
													digits,
												)}
											</>
											{"\n"}
											<>
												{" "}
												colorStrength:{" "}
												{runtimeInfo.references[1].colorStrength.toFixed(
													digits,
												)}
											</>
											{"\n"}
											<>
												{" "}
												lightStrength:{" "}
												{runtimeInfo.references[1].lightStrength.toFixed(
													digits,
												)}
											</>
											{"\n\n"}
										</>
									)}
									<>
										{distancesText2 && (
											<>
												<b>Distances to Ref 2:</b>
												{"\n" + distancesText2 + "\n"}
											</>
										)}
									</>
								</>
							)}

							{ref3 && (
								<>
									<>
										<b>
											{"\n"}
											{"Ref 3 - "}
											{ref3InProject ? (
												clickableFile(references?.[2] ?? "", ref3)
											) : (
												<>{ref3}</>
											)}
											{colorTextForRef3 + "\n"}
										</b>
									</>
									{!isAlgorithmNew && (
										<>
											<>
												{" "}
												Light bias weight:{" "}
												{lightWeights[2]?.toFixed(digits)}
											</>
											{"\n"}
											<> WB bias weight: {colorWeights[2]?.toFixed(digits)}</>
											{"\n"}
											<>
												{" "}
												Overall bias weight:{" "}
												{overallWeights[2]?.toFixed(digits)}
											</>
											{"\n\n"}
										</>
									)}
									{isAlgorithmNew && runtimeInfo?.references[2] && (
										<>
											<>
												{" "}
												LUT blend weight:{" "}
												{runtimeInfo.references[2].blendWeight.toFixed(
													digits,
												)}
											</>
											{"\n"}
											<>
												{" "}
												colorStrength:{" "}
												{runtimeInfo.references[2].colorStrength.toFixed(
													digits,
												)}
											</>
											{"\n"}
											<>
												{" "}
												lightStrength:{" "}
												{runtimeInfo.references[2].lightStrength.toFixed(
													digits,
												)}
											</>
											{"\n\n"}
										</>
									)}
									<>
										{distancesText3 && (
											<>
												<b>Distances to Ref 3:</b>
												{"\n" + distancesText3 + "\n"}
											</>
										)}
									</>
								</>
							)}
						</>
					)}

					{keyNeighbor === "self" && (
						<>
							{"\n"}
							<b>
								Cluster members
								{clusterMembers.length > 0 ? " (yellow)" : ""}
							</b>
							{`:\n${
								clusterMembers.join("\n") + (clusterMembers.length > 0 ? "\n" : "")
							}`}
						</>
					)}

					{
						<>
							{"\n"}
							<b>PresetId: </b>
							{currentImageInfo?.presetId}
							{"\n\n"}
						</>
					}
					{!isAlgorithmNew && (
						<>
							{keyNeighbor !== "self" && (
								<>
									<b>
										Auto + Bias below will be overridden by the above key
										adjustments.
									</b>
									{"\n\n"}
								</>
							)}
							<b>Auto adjustments: </b>
							{prettyPrint(
								currentImageInfo?.aiAdjustmentsMap?.[
									currentImageInfo?.presetId ?? ""
								] ?? {},
							)}
							{"\n\n"}
							<b>Non-zero biases: </b>
							{prettyPrint(currentImageInfo?.biases ?? {}, true)}
							{"\n"}
						</>
					)}

					{currentPhoto?.kind === "reference" && (
						<>
							{"\n"}
							<b>Affected photos by ref (green)</b>
							{`:\n${affectedPhotosNames.join("\n")}`}
							{affectedPhotosNames.length > 0 ? "\n" : ""}
						</>
					)}
				</pre>
			</div>
		</div>
	)
}
